import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import { ouvrirUseCase } from '../../utils/openUseCase';
import RechercheAssistant from '../../components/assistants/RechercheAssistant';
import { 
  FileText, 
  Mail, 
  MessageSquare, 
  Calendar, 
  Presentation, 
  Newspaper,
  FileCheck,
  Briefcase,
  Award,
  Globe,
  BookOpen,
  Plus,
  Search
} from 'lucide-react';
import { settingsService } from '../../services/api/settings';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const iconMap: { [key: string]: React.ElementType } = {
  FileText,
  Mail,
  MessageSquare,
  Calendar,
  Presentation,
  Newspaper,
  FileCheck,
  Briefcase,
  Award,
  Globe,
  BookOpen,
  Plus
};

const DocumentsLibrePage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Fonction pour mapper les catégories aux couleurs
  const getColorFromCategory = (category: string): string => {
    const categoryColorMap: { [key: string]: string } = {
      redaction: 'blue',
      infographie: 'purple',
      visuel: 'teal',
      presentation: 'red',
      video: 'orange',
      audio: 'cyan'
    };
    return categoryColorMap[category] || 'slate';
  };

  // Charger les use cases depuis l'API
  useEffect(() => {
    const loadUseCases = async () => {
      try {
        console.log('📋 [DEBUG] Chargement des use cases dynamiques pour documents-libre...');
        const useCasesData = await settingsService.getUseCases({ context: 'analysis' });
        console.log('✅ [DEBUG] Use cases chargés (analyse):', useCasesData.length);
        
        const transformedTemplates = useCasesData.map((uc: any) => ({
          ...uc,
          color: uc.category_color || getColorFromCategory(uc.category_code || uc.category)
        }));
        
        setTemplates(transformedTemplates);
      } catch (error) {
        console.error('❌ Erreur chargement use cases:', error);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadUseCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: { bg: string; border: string; text: string; hover: string } } = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        hover: 'hover:bg-eg-voile hover:border-blue-300'
      },
      green: {
        bg: 'bg-eg-valide/10',
        border: 'border-green-200',
        text: 'text-eg-valide',
        hover: 'hover:bg-eg-valide/10 hover:border-green-300'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100 hover:border-purple-300'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-600',
        hover: 'hover:bg-orange-100 hover:border-orange-300'
      },
      red: {
        bg: 'bg-eg-accent/10',
        border: 'border-red-200',
        text: 'text-eg-accent',
        hover: 'hover:bg-eg-accent/10 hover:border-red-300'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-600',
        hover: 'hover:bg-teal-100 hover:border-teal-300'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-600',
        hover: 'hover:bg-indigo-100 hover:border-indigo-300'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        text: 'text-pink-600',
        hover: 'hover:bg-pink-100 hover:border-pink-300'
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-600',
        hover: 'hover:bg-yellow-100 hover:border-yellow-300'
      },
      cyan: {
        bg: 'bg-cyan-50',
        border: 'border-cyan-200',
        text: 'text-cyan-600',
        hover: 'hover:bg-cyan-100 hover:border-cyan-300'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-600',
        hover: 'hover:bg-amber-100 hover:border-amber-300'
      },
      slate: {
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        text: 'text-slate-600',
        hover: 'hover:bg-slate-100 hover:border-slate-300'
      }
    };

    return colorMap[color] || colorMap.slate;
  };

  // Filtrer les templates basés sur la recherche uniquement
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Filtrer par recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = templates.filter(t => {
        const title = String(t.title || t.name || '').toLowerCase();
        const description = String(t.description || '').toLowerCase();
        const category = String(t.category_name || t.category || '').toLowerCase();
        const matches = title.includes(query) || description.includes(query) || category.includes(query);
        return matches;
      });
      console.log('🔍 Filtrage:', { query, total: templates.length, filtered: filtered.length });
    } else {
      filtered = templates;
    }

    return filtered;
  }, [templates, searchQuery]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-eg-encre mb-2">
            Gestion de Connaissances Libre
          </h1>
          <p className="text-eg-gris">
            Explorez et utilisez des modèles pour créer du contenu visuel, des présentations et d'autres ressources de connaissances
          </p>
        </div>

        {/* Orientation : décrire son besoin plutôt que parcourir les vignettes */}
        <div className="mb-8">
          <RechercheAssistant />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            {/* Section de recherche et filtres */}
            <div className="mb-8 space-y-4">
              {/* Barre de recherche */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-eg-estompe" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    console.log('🔍 Recherche mise à jour:', value);
                  }}
                  placeholder="Rechercher un modèle..."
                  className="block w-full pl-10 pr-3 py-3 border border-[#DDE2DD] rounded-md leading-5 bg-eg-blanc placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

            </div>

            {/* Résultats */}
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-eg-estompe mb-4">
                  <Search className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-semibold text-eg-encre mb-2">
                  Aucun résultat trouvé
                </h3>
                <p className="text-eg-gris mb-4">
                  {searchQuery ? `Aucun modèle ne correspond à "${searchQuery}"` : "Aucun modèle disponible"}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-eg-accent hover:text-eg-brun font-medium"
                  >
                    Réinitialiser la recherche
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Templates Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTemplates.map((template) => {
                    const colors = getColorClasses(template.color);
                    // Utiliser l'icône de l'API ou une icône par défaut
                    const Icon = template.icon_name && iconMap[template.icon_name] 
                      ? iconMap[template.icon_name] 
                      : FileText;
                
                    return (
                      <div 
                        key={template.id}
                        onClick={() => ouvrirUseCase(template, navigate)}
                      >
                        <Card 
                          className={`${colors.bg} ${colors.border} border-2 transition-all duration-200 ${colors.hover} cursor-pointer`}
                          padding="none"
                        >
                          {/* Image ou icône par défaut */}
                          {(template.image_url || template.image) ? (
                            <div className="relative w-full h-48 overflow-hidden rounded-t-lg">
                              <img 
                                src={template.image_url || template.image} 
                                alt={template.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className={`w-full h-48 ${colors.bg} flex items-center justify-center rounded-t-lg border-b-2 ${colors.border}`}>
                              <Icon className={`w-16 h-16 ${colors.text} opacity-50`} />
                            </div>
                          )}
                          
                          {/* Contenu du card */}
                          <div className="p-6">
                            <h3 className={`${colors.text} text-lg font-semibold mb-2`}>
                              {template.title}
                            </h3>
                            <p className="text-eg-gris text-sm">
                              {template.summary || template.description}
                            </p>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>

                {/* Info Section */}
                <div className="mt-12 bg-gradient-to-r from-primary-50 to-purple-50 rounded-md p-6 border border-primary-100">
                  <h2 className="text-xl font-semibold text-eg-encre mb-3">
                    💡 Comment ça fonctionne ?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-start">
                      <div className="bg-eg-ombre text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        1
                      </div>
                      <div>
                        <h3 className="font-semibold text-eg-encre mb-1">Choisissez un modèle</h3>
                        <p className="text-sm text-eg-gris">
                          Sélectionnez le type de contenu que vous souhaitez créer
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-eg-ombre text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        2
                      </div>
                      <div>
                        <h3 className="font-semibold text-eg-encre mb-1">Personnalisez</h3>
                        <p className="text-sm text-eg-gris">
                          Remplissez les informations ou décrivez votre besoin
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-eg-ombre text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        3
                      </div>
                      <div>
                        <h3 className="font-semibold text-eg-encre mb-1">Génération IA</h3>
                        <p className="text-sm text-eg-gris">
                          L'IA crée votre contenu que vous pouvez éditer et affiner
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DocumentsLibrePage;
